import { memo } from "react";
import { useIntl } from "react-intl";
import { components, type GroupBase, type SingleValueProps } from "react-select";

import Input from "components/v2/Input/Input";
import type { SelectOption } from "components/v2/Select/types";
import PackingListCell from "@RequestFormV2/sections/PackingList/components/shared/PackingListCell";
import PackingListSelect from "@RequestFormV2/sections/PackingList/components/shared/PackingListSelect";
import PackingListTableRow from "@RequestFormV2/sections/PackingList/components/shared/PackingListTableRow";
import CommodityCurrencyMenuPortal from "@RequestFormV2/sections/PackingList/components/CommoditiesSection/CommodityCurrencyMenuPortal";
import type { GoodCustomsInformation, PackingListRow } from "@RequestFormV2/sections/PackingList/types";
import { COMMODITY_COLUMN_WIDTHS } from "@RequestFormV2/sections/PackingList/components/shared/packingListColumnWidths";
import { usePackingList } from "@RequestFormV2/sections/PackingList/context";
import { getCommodityFieldPath } from "@RequestFormV2/sections/PackingList/utils/fieldPaths";
import { getCommodityParentPickerValue } from "@RequestFormV2/sections/PackingList/utils/commodityProjection";

function CurrencyCodeSingleValue(props: SingleValueProps<SelectOption, false, GroupBase<SelectOption>>) {
  return <components.SingleValue {...props}>{props.data.value}</components.SingleValue>;
}

type CommodityTableRowProps = {
  packingListIndex: number;
  commodityIndex: number;
  commodityKey: string;
  commodity: GoodCustomsInformation;
  parentRow: PackingListRow;
  rowNumber: number;
  deleteDisabled: boolean;
  commodityParentPickerOptions: SelectOption[];
};

function CommodityTableRow({
  packingListIndex,
  commodityIndex,
  commodityKey,
  commodity,
  parentRow,
  rowNumber,
  deleteDisabled,
  commodityParentPickerOptions,
}: CommodityTableRowProps) {
  const intl = useIntl();
  const {
    currencyOptions,
    countryOptions,
    duplicateCommodity,
    deleteCommodity,
    handleCommodityFieldBlur,
    moveCommodityToParent,
  } = usePackingList();

  return (
    <PackingListTableRow
      rowNumber={rowNumber}
      onDuplicate={() => duplicateCommodity(packingListIndex, commodityIndex)}
      onDelete={() => deleteCommodity(packingListIndex, commodityIndex)}
      deleteDisabled={deleteDisabled}
    >
      <PackingListCell style={{ width: COMMODITY_COLUMN_WIDTHS.parentPicker }}>
        <PackingListSelect
          isSearchable={false}
          testId={`commodity-parent-${commodityKey}`}
          value={getCommodityParentPickerValue(packingListIndex, commodity, parentRow)}
          options={commodityParentPickerOptions}
          onChange={(value) => {
            if (!value) return;
            moveCommodityToParent(packingListIndex, commodityIndex, Number(value));
          }}
          placeholder={intl.formatMessage({
            id: "request-form.packing-list.selectHandlingUnit",
            defaultMessage: "Select handling unit",
          })}
        />
      </PackingListCell>
      <PackingListCell>
        <Input
          name={getCommodityFieldPath(packingListIndex, commodityIndex, "description")}
          onChange={() => handleCommodityFieldBlur(packingListIndex, commodityIndex)}
        />
      </PackingListCell>
      <PackingListCell style={{ width: COMMODITY_COLUMN_WIDTHS.originCountry }}>
        <PackingListSelect
          isSearchable
          isClearable
          testId={`commodity-origin-country-${commodityKey}`}
          name={getCommodityFieldPath(packingListIndex, commodityIndex, "origin_country")}
          options={countryOptions}
          placeholder={intl.formatMessage({
            id: "request-form.packing-list.originCountryPlaceholder",
            defaultMessage: "Country",
          })}
          onChange={() => handleCommodityFieldBlur(packingListIndex, commodityIndex)}
        />
      </PackingListCell>
      <PackingListCell style={{ width: COMMODITY_COLUMN_WIDTHS.hsCode }}>
        <Input
          name={getCommodityFieldPath(packingListIndex, commodityIndex, "hs_code")}
          onChange={() => handleCommodityFieldBlur(packingListIndex, commodityIndex)}
        />
      </PackingListCell>
      <PackingListCell
        className="rfv2-packing-list-table__cell--value"
        style={{ width: COMMODITY_COLUMN_WIDTHS.valueColumn }}
      >
        <Input
          name={getCommodityFieldPath(packingListIndex, commodityIndex, "value")}
          type="number"
          placeholder="0.00"
          onChange={() => handleCommodityFieldBlur(packingListIndex, commodityIndex)}
        />
        <div className="rfv2-packing-list-table__currency-select">
          <PackingListSelect
            isSearchable
            menuPlacement="bottom"
            name={getCommodityFieldPath(packingListIndex, commodityIndex, "currency_code")}
            options={currencyOptions}
            components={{
              MenuPortal: CommodityCurrencyMenuPortal,
              SingleValue: CurrencyCodeSingleValue,
            }}
            onChange={() => handleCommodityFieldBlur(packingListIndex, commodityIndex)}
          />
        </div>
      </PackingListCell>
    </PackingListTableRow>
  );
}

export default memo(CommodityTableRow);
